// The maths behind the floating calendar's month grid, with no React in it so it can be tested on its own.
//
// Everything here works on ABSOLUTE days (a Date at 00:00 UTC, what tools/calendars.js calls a day) and only
// reads them through a calendar - Gregorian, Shamsi (jalali) or Hijri - when it needs a month or a year.

import { partsOf, fromParts, daysInMonth, normalizeDateSystem } from '../calendars';

const DAY = 24 * 60 * 60 * 1000;

// The first day of the week, 0 = Sunday. Iran's week starts on Saturday; the Gulf's, and the Gregorian one here, on Sunday.
export const WEEK_START = { gregorian: 0, jalali: 6, hijri: 0 };

export const sameDay = (a, b) => Boolean(a && b) && a.getTime() === b.getTime();
export const addDays = (d, n) => new Date(d.getTime() + n * DAY);

/** Whole days from a to b, both ends counted: the same day twice is 1. */
export const rangeDays = (a, b) => Math.round((b.getTime() - a.getTime()) / DAY) + 1;

/**
 * The month `cursor` falls in, as 42 cells (six weeks, so the grid never changes height from month to month).
 * Each cell: { date, day (the number written in the cell, in this calendar), inMonth }.
 */
export function monthGrid(cursor, system) {
  const sys = normalizeDateSystem(system);
  const p = partsOf(cursor, sys);
  const first = fromParts({ year: p.year, month: p.month, day: 1 }, sys);
  const dim = daysInMonth(p.year, p.month, sys);
  const offset = (first.getUTCDay() - WEEK_START[sys] + 7) % 7;
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const date = addDays(first, i - offset);
    cells.push({ date, day: partsOf(date, sys).day, inMonth: i >= offset && i < offset + dim });
  }
  return { year: p.year, month: p.month, dim, offset, cells };
}

/** The same day-of-month `delta` months on (clamped to the month's length: 30 Esfand + 1 year is 29 Esfand). Unchanged if the date does not exist. */
export function shiftMonth(cursor, system, delta) {
  const sys = normalizeDateSystem(system);
  const p = partsOf(cursor, sys);
  if (!p) return cursor;
  const index = p.year * 12 + (p.month - 1) + delta;
  const year = Math.floor(index / 12);
  const month = (((index % 12) + 12) % 12) + 1;
  const day = Math.min(p.day, daysInMonth(year, month, sys));
  return fromParts({ year, month, day }, sys) || cursor;
}

export const shiftYear = (cursor, system, delta) => shiftMonth(cursor, system, delta * 12);

/** The same day in another month of the same year. */
export function withMonth(cursor, system, month) {
  const sys = normalizeDateSystem(system);
  const p = partsOf(cursor, sys);
  return fromParts({ year: p.year, month, day: Math.min(p.day, daysInMonth(p.year, month, sys)) }, sys) || cursor;
}

/** The same day and month in another year. */
export function withYear(cursor, system, year) {
  const sys = normalizeDateSystem(system);
  const p = partsOf(cursor, sys);
  return fromParts({ year, month: p.month, day: Math.min(p.day, daysInMonth(year, p.month, sys)) }, sys) || cursor;
}

/** The twelve years of the page `year` is on (a year picker's page): { start, years }. */
export function yearPage(year) {
  const start = Math.floor(year / 12) * 12;
  return { start, years: Array.from({ length: 12 }, (_, i) => start + i) };
}

/** The seven weekday names, starting on the calendar's first day of the week. */
export function weekdayNames(system, lang = 'en', style = 'short') {
  const sys = normalizeDateSystem(system);
  const fmt = new Intl.DateTimeFormat(`${lang}-u-nu-latn`, { weekday: style, timeZone: 'UTC' });
  // 2023-01-01 was a Sunday
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2023, 0, 1 + ((WEEK_START[sys] + i) % 7)))));
}
